<div class="space-y-6">
  <!-- Header -->
  <div class="bg-white border border-gray-200 rounded-xl p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between shadow-sm">
    <div class="flex items-start space-x-4">
      <div class="w-14 h-14 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xl font-semibold">
        {{ patient()?.firstName?.charAt(0) }}{{ patient()?.lastName?.charAt(0) }}
      </div>
      <div>
        <div class="flex items-center space-x-3">
          <h1 class="text-2xl font-semibold text-gray-900">{{ patient()?.firstName }} {{ patient()?.lastName }}</h1>
          <span class="bg-primary-50 text-primary-700 px-2 py-0.5 rounded text-xs font-medium">{{ patient()?.gender }}</span>
          <span class="text-sm text-gray-500">{{ patient()?.dateOfBirth | date:'mediumDate' }}</span>
        </div>
        <div class="flex items-center space-x-3 text-sm text-gray-500 mt-1">
          <span>ID: {{ patientId() || '—' }}</span>
          <span>Phone: {{ patient()?.phone || '—' }}</span>
          <a class="text-primary-600 hover:text-primary-700" href="mailto:{{ patient()?.email }}">{{ patient()?.email }}</a>
        </div>
      </div>
    </div>
    <div class="mt-4 lg:mt-0 flex flex-wrap gap-2">
      <app-button variant="secondary">Video Call</app-button>
      <app-button variant="primary" (clicked)="startConsult()">Start Consult</app-button>
      <app-button variant="secondary">Use Template</app-button>
      <app-button variant="secondary">Follow-up / Repeat</app-button>
      <app-button variant="secondary">Vaccination</app-button>
      <app-button variant="secondary">Certificates</app-button>
      <app-button variant="secondary">Add Bill / Payment</app-button>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Queue Column -->
    <div class="bg-white border border-gray-200 rounded-xl shadow-sm">
      <div class="p-4 border-b border-gray-200 space-y-3">
        <div class="flex items-center space-x-2">
          <div class="relative flex-1">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <app-input type="text" placeholder="Search queue" class="pl-9"></app-input>
          </div>
          <app-button variant="secondary" size="sm">
            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {{ displayDate() | date:'dd MMM yyyy' }}
          </app-button>
        </div>
        <nav class="flex">
          <button class="flex-1 px-4 py-3 text-sm font-semibold text-primary-600 border-b-2 border-primary-600">Queue ({{ queueAppointments().length }})</button>
          <button class="flex-1 px-4 py-3 text-sm text-gray-500">Seen ({{ seenAppointments().length }})</button>
          <button class="flex-1 px-4 py-3 text-sm text-gray-500">Cancelled ({{ cancelledAppointments().length }})</button>
        </nav>
      </div>
      <div class="p-4 space-y-3 h-64 overflow-y-auto">
        @if (queueAppointments().length === 0) {
          <div class="text-center text-gray-500 text-sm py-8">Today's queue is empty</div>
        } @else {
          @for (appointment of queueAppointments(); track appointment.id) {
            <div class="border border-gray-200 rounded-lg p-3 hover:border-primary-300 transition">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-semibold text-gray-900">{{ appointment.time }} • {{ appointment.type }}</p>
                  <p class="text-xs text-gray-500">{{ appointment.reason || 'General Checkup' }}</p>
                </div>
                <span class="badge badge-primary">{{ appointment.status }}</span>
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- Quick Insights Column -->
    <div class="space-y-4">
      <div class="card h-full">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Patient Timeline</h3>
        <p class="text-sm text-gray-500">Check the efficiency of the treatment</p>
        <div class="mt-6 border border-dashed border-gray-200 rounded-lg py-6 text-center text-gray-400 text-sm">
          No data yet
        </div>
      </div>
      <div class="card">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Vitals Trend</h3>
        <div class="border border-dashed border-gray-200 rounded-lg py-10 text-center text-gray-400 text-sm">
          No Data
        </div>
      </div>
      <div class="card">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Vaccinations</h3>
        <div class="border border-dashed border-gray-200 rounded-lg py-8 text-center text-gray-400 text-sm">
          No vaccinations recorded
        </div>
      </div>
    </div>

    <!-- Right Pane -->
    <div class="space-y-4">
      <div class="card h-60 flex items-center justify-center text-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Start consult to create a prescription</h2>
          <p class="text-sm text-gray-500 mt-2">Prepare notes, vitals and medication recommendations in one place.</p>
          <app-button variant="primary" (clicked)="startConsult()" class="mt-6">Start Consultation</app-button>
        </div>
      </div>
      <div class="card">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500">Next appointment</p>
            <p class="text-base font-semibold text-gray-900">{{ queueAppointments()[0]?.time || 'No upcoming appointments' }}</p>
          </div>
          <app-button variant="secondary" size="sm">View Calendar</app-button>
        </div>
      </div>
      <div class="card">
        <h3 class="text-sm font-semibold text-gray-700 mb-3">Patient Notes</h3>
        <p class="text-sm text-gray-500">No notes yet. Start a consultation to add clinical findings.</p>
      </div>
    </div>
  </div>
</div>

